import React, { useState, useRef } from 'react';

import moment, { Moment } from 'moment';

import { Checkbox, FormControlLabel } from '@material-ui/core';
import { KeyboardDatePicker, KeyboardDatePickerProps } from '@material-ui/pickers';

import { DEFAULT_MAX_DATE, DEFAULT_MIN_DATE, FORMAT_STRING } from '../util/date-utils';
import { SmartlookEventInformation, trackWithSmartlook } from '../util/track-with-smartlook';

export enum DatePickerError {
  DATE_PICKER_MAX_DATE = 'DATE_PICKER_MAX_DATE',
  DATE_PICKER_MIN_DATE = 'DATE_PICKER_MIN_DATE',
  DATE_PICKER_REQUIRED = 'DATE_PICKER_REQUIRED',
  DATE_PICKER_INVALID = 'DATE_PICKER_INVALID',
}

type InputProps = {
  /**
   * Callback to get valid moment date changes. `Date` may still cause a
   * validation error like being beyond max or min date. Use `onError` to
   * detect errors from this component.
   * @param date a valid moment or null
   */
  onChange: (date: Moment | null) => void;
  /**
   * Callback to detect invalid changes to the date picker. The errors are
   * defined by the enum `DatePickerError`.
   * @param error A string from the `DatePickerError` enum
   */
  onError?: (error: DatePickerError | null) => void;
  /**
   * Populate the date picker with a default date. Otherwise, it will be empty.
   * This prop only changes the initial date and does not affect the date after
   * render.
   */
  defaultDate?: Moment | Date | string | null;
  /**
   * The label for the optional `No Date` checkbox. Provide this label to
   * render the checkbox with the date picker.
   */
  noDateCheckboxLabel?: string;
  /**
   * Enable the toolbar at the top of the calendar picker. Useful for quickly changing the year.
   */
  enableToolbar?: boolean;
  /**
   * Leave space for the error message under the picker
   */
  bottomPadding?: boolean;
  /**
   * Disable date picker
   */
  disabled?: boolean;
  /**
   * Use for creating a custom event in Smartlook to track usage of the onClick event.
   * @see {@link https://teamengine.atlassian.net/wiki/spaces/PD/pages/2700804314/Using+Custom+Smartlook+Events Using Custom Smartlook Events}
   * @example
   * eventName: 'User Identification Response',
   * props: { action: 'submit' | 'skip', ... },
   */
  smartlookEvent?: SmartlookEventInformation;
};

type Overrides =
  | keyof InputProps
  | 'value'
  | 'onChange'
  | 'format'
  | 'placeholder'
  | 'disableToolbar'
  | 'onError'
  | 'error'
  | 'helperText'
  | 'defaultValue';

type Props = InputProps & Omit<KeyboardDatePickerProps, Overrides>;

/**
 * Our customization of material ui's date picker with keyboard functionality. Most of
 * the props that work with the material-ui `KeyboardDatePicker` will be passed through to that
 * component.
 */
export const UiDatePicker = ({
  defaultDate,
  label,
  noDateCheckboxLabel,
  onChange,
  onError,
  required,
  maxDate,
  smartlookEvent,
  disabled,
  minDate,
  disablePast,
  disableFuture,
  maxDateMessage,
  minDateMessage,
  onBlur,
  variant = 'inline',
  enableToolbar = false,
  bottomPadding,
  ...pickerProps
}: Props) => {
  const [date, setDate] = useState<Moment | null>(
    defaultDate && moment(defaultDate).isValid() ? moment(defaultDate) : null,
  );
  const [hasNoDate, setHasNoDate] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined | null>(null);

  const TODAYS_DATE = moment();
  const MIN_DATE = disablePast ? TODAYS_DATE : minDate ? moment(minDate) : DEFAULT_MIN_DATE;
  const MAX_DATE = disableFuture ? TODAYS_DATE : maxDate ? moment(maxDate) : DEFAULT_MAX_DATE;

  const lastValidDate = useRef(date);
  if (date?.isValid()) {
    lastValidDate.current = date;
  }

  const callOnError = (err: DatePickerError | null) => {
    if (onError) {
      onError(err);
    }
  };

  const checkErrors = (newDate: Moment | null) => {
    if (!newDate) {
      if (required) {
        callOnError(DatePickerError.DATE_PICKER_REQUIRED);
      } else {
        callOnError(null);
      }
    } else if (!newDate.isValid()) {
      callOnError(DatePickerError.DATE_PICKER_INVALID);
    } else if (newDate.isBefore(MIN_DATE, 'd')) {
      callOnError(DatePickerError.DATE_PICKER_MIN_DATE);
    } else if (newDate.isAfter(MAX_DATE, 'd')) {
      callOnError(DatePickerError.DATE_PICKER_MAX_DATE);
    } else {
      callOnError(null);
    }
  };

  const handleDateChange = (newDate: Moment | null) => {
    if (smartlookEvent) {
      trackWithSmartlook(smartlookEvent);
    }
    checkErrors(newDate);
    setDate(newDate);
    if (!newDate) {
      onChange(null);
      setHasNoDate(true);
    } else if (newDate.isValid()) {
      onChange(newDate);
      setHasNoDate(false);
    }
  };

  const handleDateBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (hasNoDate) {
      e.target.value = '';
    } else {
      e.target.value = lastValidDate.current?.toISOString() ?? '';
    }
    if (onBlur) {
      onBlur(e);
    }
  };

  // Always runs after value changes in KeyboardDatePicker
  const handleError = (err: React.ReactNode) => {
    // Err is undefined when the input is valid or null. Since this runs as a
    // side effect of date changing, we can check date and set a custom error.
    if (required && !date) {
      setError('Required');
    } else {
      setError(err?.toString());
    }
  };

  const handleHasNoDate = (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      handleDateChange(null);
    } else {
      handleDateChange(lastValidDate.current);
    }
  };

  return (
    <div className="u-display-flex u-flexDirection-column">
      <KeyboardDatePicker
        {...pickerProps}
        required={required}
        value={date}
        onChange={handleDateChange}
        onBlur={handleDateBlur}
        format={FORMAT_STRING}
        placeholder={FORMAT_STRING}
        label={label}
        variant={variant}
        disableToolbar={!enableToolbar}
        disablePast={disablePast}
        minDate={MIN_DATE}
        minDateMessage={minDateMessage || 'Date is too far in the past'}
        disableFuture={disableFuture}
        maxDate={MAX_DATE}
        maxDateMessage={
          maxDateMessage ||
          (disableFuture ? 'Date cannot be in the future' : 'Date is too far in the future')
        }
        onError={handleError}
        error={Boolean(error)}
        helperText={error || (bottomPadding ? ' ' : error)}
        disabled={disabled}
      />
      {noDateCheckboxLabel && (
        <FormControlLabel
          control={<Checkbox checked={hasNoDate} onChange={handleHasNoDate} />}
          label={noDateCheckboxLabel}
        />
      )}
    </div>
  );
};
